import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  ArrowRight,
  CheckCircle2,
  FileText,
  Search,
  MessagesSquare,
  Award,
} from "lucide-react";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same visual system as Work.jsx / Home.jsx — identical theme tokens so
// this page reads as one product, not a bolted-on page.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const jobs = [
  { title: "Frontend Developer", location: "On-Site / Gurugram", type: "Full-time" },
  { title: "Backend Developer", location: "On-Site / Gurugram", type: "Full-time" },
  { title: "UI/UX Designer", location: "On-Site / Gurugram", type: "Full-time" },
  { title: "Mobile App Developer", location: "On-Site / Gurugram", type: "Full-time" },
];

const benefits = [
  "Flexible working hours",
  "Health insurance & wellness",
  "Professional growth & training",
  "Team outings & events",
  "Remote work opportunities",
];

const hiringSteps = [
  { step: "01", title: "Application", icon: FileText },
  { step: "02", title: "Screening", icon: Search },
  { step: "03", title: "Interview", icon: MessagesSquare },
  { step: "04", title: "Offer", icon: Award },
];

const valueItems = ["Growth", "Ownership", "Flexibility", "Craft", "Impact", "Team"];

// Reusable eyebrow — identical to Work's / Home's.
function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// Values marquee — same infinite-loop strip pattern as Work's TrustBand,
// different content so it doesn't feel copy-pasted.
function ValuesBand() {
  return (
    <div
      className="relative py-8 overflow-hidden border-y"
      style={{ backgroundColor: theme.darkBandDeep, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <style>{`
        @keyframes careersMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .careers-marquee-track { animation: careersMarquee 20s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .careers-marquee-track { animation: none; } }
      `}</style>
      <div className="flex w-max careers-marquee-track">
        {[...valueItems, ...valueItems].map((item, i) => (
          <span key={i} className="px-10 text-lg font-bold whitespace-nowrap" style={{ color: "rgba(255,255,255,0.35)" }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// NEW hero background — completely different animation language from
// Work's aurora/beam field: an "ascending growth" motif. Bokeh particles
// rise and fade like sparks, a core glow breathes, upward light columns
// sweep, and a dashed ring slowly orbits a fixed point. No blobs, no
// beams, no grid floor — distinct silhouette and motion.
// ---------------------------------------------------------------------
function GrowthField() {
  const particles = Array.from({ length: 22 }).map((_, i) => ({
    left: (i * 47) % 100,
    delay: (i % 11) * 0.6,
    duration: 8 + (i % 6),
    size: i % 4 === 0 ? 5 : i % 3 === 0 ? 3 : 2,
    gold: i % 3 === 0,
  }));

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="growth-core" />
      <span className="growth-core-ring" />
      <span className="growth-core-ring growth-core-ring-2" />
      <span className="growth-orbit" />
      <span className="growth-column growth-column-1" />
      <span className="growth-column growth-column-2" />
      <span className="growth-column growth-column-3" />
      <span className="growth-dots" />

      {particles.map((p, i) => (
        <span
          key={i}
          className="growth-particle"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.gold ? theme.gold : "#FFFFFF",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            opacity: p.gold ? 0.55 : 0.35,
          }}
        />
      ))}
    </div>
  );
}

export default function Careers() {
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        /* Breathing core glow at hero center */
        .growth-core {
          position: absolute; top: 48%; left: 68%; width: 26rem; height: 26rem;
          margin: -13rem 0 0 -13rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}55, transparent 70%);
          filter: blur(50px);
          animation: growthCoreBreathe 6s ease-in-out infinite;
        }
        @keyframes growthCoreBreathe {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.25); opacity: 1; }
        }

        /* Two concentric dashed rings that counter-rotate around the core */
        .growth-core-ring {
          position: absolute; top: 48%; left: 68%; width: 16rem; height: 16rem;
          margin: -8rem 0 0 -8rem; border-radius: 9999px;
          border: 1px dashed rgba(255,255,255,0.18);
          animation: growthRingSpin 30s linear infinite;
        }
        .growth-core-ring-2 {
          width: 22rem; height: 22rem; margin: -11rem 0 0 -11rem;
          border-color: rgba(217,164,65,0.22);
          animation: growthRingSpin 42s linear infinite reverse;
        }
        @keyframes growthRingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* Single accent dot orbiting the core on its own path */
        .growth-orbit {
          position: absolute; top: 48%; left: 68%; width: 19rem; height: 19rem;
          margin: -9.5rem 0 0 -9.5rem;
          animation: growthOrbitSpin 14s linear infinite;
        }
        .growth-orbit::after {
          content: ""; position: absolute; top: 0; left: 50%; width: 8px; height: 8px;
          margin-left: -4px; border-radius: 9999px; background: ${theme.accent};
          box-shadow: 0 0 12px 3px ${theme.accent}aa;
        }
        @keyframes growthOrbitSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* Soft vertical light columns, like stage light beams standing upright */
        .growth-column {
          position: absolute; bottom: -10%; width: 5px; height: 70%;
          background: linear-gradient(to top, rgba(230,57,70,0.35), transparent);
          filter: blur(6px);
          animation: growthColumnPulse 5s ease-in-out infinite;
        }
        .growth-column-1 { left: 12%; animation-delay: 0s; }
        .growth-column-2 { left: 30%; background: linear-gradient(to top, rgba(217,164,65,0.3), transparent); animation-delay: 1.6s; }
        .growth-column-3 { left: 20%; animation-delay: 3.1s; }
        @keyframes growthColumnPulse {
          0%, 100% { opacity: 0.25; transform: scaleY(0.85); }
          50% { opacity: 0.7; transform: scaleY(1.1); }
        }

        .growth-dots {
          position: absolute; inset: -10px; opacity: 0.05;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px); background-size: 26px 26px;
          animation: growthDotsDrift 45s linear infinite;
        }
        @keyframes growthDotsDrift { from { background-position: 0 0; } to { background-position: -260px 260px; } }

        /* Rising, fading spark particles — the ascension/growth motif */
        .growth-particle {
          position: absolute; bottom: -5%; border-radius: 9999px;
          animation-name: growthParticleRise;
          animation-timing-function: ease-in;
          animation-iteration-count: infinite;
        }
        @keyframes growthParticleRise {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          80% { opacity: 0.6; }
          100% { transform: translateY(-120vh) translateX(1.5rem); opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .growth-core, .growth-core-ring, .growth-core-ring-2, .growth-orbit,
          .growth-column, .growth-dots, .growth-particle { animation: none !important; }
        }

        .card-glow {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
      `}</style>

      {/* ================= HERO (dark, new growth-field bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-28 md:pt-48 md:pb-36" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <GrowthField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow center light>We're Hiring</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Build your career at" className="text-white" />
              <br />
              <span style={{ color: theme.accent }}>
                <SplitText text="UniBcomp" delay={0.25} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              Explore exciting opportunities, grow your skills, and be part of a dynamic team.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                
               <a   href="#open-positions"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  View Open Positions
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Talk to Us
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ValuesBand />

      {/* ================= OPEN POSITIONS (light) ================= */}
      <section id="open-positions" className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Careers</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Open" /> <span style={{ color: theme.accent }}>Positions</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {jobs.map((job) => (
              <motion.div
                key={job.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="group relative p-7 rounded-2xl border overflow-hidden transition-colors duration-300"
                style={{ backgroundColor: theme.surface, borderColor: theme.border }}
              >
                <span className="card-glow" />
                <div className="relative flex items-start justify-between gap-4 mb-4">
                  <h3 className="text-xl font-semibold" style={{ color: theme.ink }}>
                    {job.title}
                  </h3>
                  <span
                    className="shrink-0 text-xs px-3 py-1 rounded-full font-semibold"
                    style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                  >
                    {job.type}
                  </span>
                </div>
                <p className="relative flex items-center gap-2 text-sm" style={{ color: theme.inkMuted }}>
                  <MapPin size={16} style={{ color: theme.accent }} />
                  {job.location}
                </p>
                <Link
                  to="/apply"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="relative mt-6 inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Apply Now
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= WHY JOIN US (dark band) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <RevealSection>
            <Eyebrow center light>Culture & Benefits</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">
              <SplitText text="Why join" /> <span style={{ color: theme.accent }}>UniBcomp?</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-14 grid md:grid-cols-3 gap-6 text-left"
          >
            {benefits.map((benefit) => (
              <motion.div
                key={benefit}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className="flex items-center gap-3 p-6 rounded-2xl border transition-colors duration-300"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <CheckCircle2 size={22} style={{ color: theme.accent }} className="shrink-0" />
                <h3 className="text-base font-semibold text-white">{benefit}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= HIRING PROCESS (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <RevealSection>
            <Eyebrow center>Step by Step</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Our Hiring" /> <span style={{ color: theme.accent }}>Process</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-16 grid md:grid-cols-4 gap-6"
          >
            {hiringSteps.map((step) => (
              <motion.div
                key={step.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="group relative p-8 rounded-2xl border flex flex-col items-center overflow-hidden transition-colors duration-300"
                style={{ backgroundColor: theme.surface, borderColor: theme.border }}
              >
                <span className="card-glow" />
                <span className="absolute top-4 right-5 text-sm font-mono" style={{ color: theme.border }}>
                  {step.step}
                </span>
                <div
                  className="relative w-14 h-14 flex items-center justify-center rounded-xl mb-4"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  <step.icon size={26} />
                </div>
                <h3 className="relative text-xl font-semibold" style={{ color: theme.ink }}>{step.title}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Join Us</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Ready to" />
            <span className="block">
              <span style={{ color: theme.accent }}>
                <SplitText text="grow with us?" delay={0.15} />
              </span>
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Join our dynamic team and work on exciting projects that make an impact.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Join Our Team
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}